import { Capacitor } from '@capacitor/core';
import apiService from './ApiService';
import { debugLog, debugError } from '../utils/debugUtils';

class FirebaseMessagingService {
  constructor() {
    this.messaging = null;
    this.token = null;
    this._initialized = false;
    this._isNative = Capacitor.isNativePlatform();
  }

  async initialize() {
    const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
    if (!apiKey) {
      debugLog('[FirebaseMessagingService] Firebase not configured');
      return;
    }
    if (this._initialized) return;

    try {
      if (this._isNative) {
        await this._initializeNative();
      } else {
        await this._initializeWeb();
      }
      this._initialized = true;
    } catch (err) {
      debugError('[FirebaseMessagingService] Init error:', err);
    }
  }

  async _initializeNative() {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    const { receive } = await FirebaseMessaging.requestPermissions();
    debugLog('[FirebaseMessagingService] Native permission:', receive);
    if (receive !== 'granted') return;

    this.token = await this._getNativeTokenWithRetry(FirebaseMessaging);
    if (!this.token) return;
    debugLog('[FirebaseMessagingService] Native FCM token obtained');

    FirebaseMessaging.addListener('notificationReceived', (event) => {
      debugLog('[FirebaseMessagingService] Foreground native message:', event);
    });
  }

  async _getNativeTokenWithRetry(FirebaseMessaging, retries = 5, delayMs = 1000) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const { token } = await FirebaseMessaging.getToken();
        return token;
      } catch (err) {
        // On iOS the APNS device token arrives asynchronously after requestPermissions();
        // getToken() fails with "No APNS token specified" if called before it lands.
        if (attempt === retries) {
          debugError('[FirebaseMessagingService] No se pudo obtener el token FCM:', err);
          return null;
        }
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    return null;
  }

  async _initializeWeb() {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };

    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    this.messaging = getMessaging(app);

    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      debugLog('[FirebaseMessagingService] Web notification permission:', permission);
    }

    if (Notification.permission === 'granted') {
      this.token = await getToken(this.messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });
      debugLog('[FirebaseMessagingService] Web FCM token obtained');
    }

    onMessage(this.messaging, (payload) => {
      debugLog('[FirebaseMessagingService] Web foreground message:', payload);
      this._showForegroundNotification(payload);
    });
  }

  async registerToken() {
    if (this.token) {
      await this._registerTokenWithBackend(this.token);
    }
  }

  async _registerTokenWithBackend(token) {
    if (!token) return;
    try {
      await apiService.post('/api/v1/notifications/device-token', { token });
      debugLog('[FirebaseMessagingService] Token registrado en backend');
    } catch (err) {
      debugError('[FirebaseMessagingService] Error registrando token en backend:', err);
    }
  }

  async _showForegroundNotification(payload) {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const title = payload.notification?.title || 'Concordare';
    const options = {
      body: payload.notification?.body || '',
      icon: '/assets/icons/icon-192.webp',
    };
    new Notification(title, options);
  }

  async removeToken() {
    if (!this.token) return;
    try {
      if (this._isNative) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        await FirebaseMessaging.deleteToken();
      }
      await apiService.request('DELETE', '/api/v1/notifications/device-token', { token: this.token });
      this.token = null;
    } catch (err) {
      debugError('[FirebaseMessagingService] Error eliminando token:', err);
    }
  }

  getToken() {
    return this.token;
  }
}

export default new FirebaseMessagingService();
